import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import NotificationBell from "@/components/NotificationBell";
import {
  LayoutDashboard, Building2, Image, PenTool,
  CheckCircle, Calendar, Cpu, Share2, LogOut, Sparkles, X,
  CalendarRange, Megaphone, FileText, BarChart3, Users, Crosshair, Globe, Moon, Sun, Webhook, Store, LineChart, MessageSquareHeart, UserCog, Mail, KeyRound, ChevronRight, ChevronDown, Inbox, Trophy, ShieldCheck, Palette, Activity, Target, Network, CircleDollarSign, UserPlus, ListChecks, Radar, Compass
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";

const navSections = [
  {
    items: [
      { path: "/gruppe", tKey: "nav.group", icon: Network, fallback: "InvictaX Gruppe" },
      { path: "/", tKey: "nav.dashboard", icon: LayoutDashboard },
      { path: "/firmen", tKey: "nav.companies", icon: Building2 },
      { path: "/setup", tKey: "nav.setup", icon: ListChecks, fallback: "Setup-Anleitung" },
      { path: "/medien", tKey: "nav.media", icon: Image },
    ]
  },
  {
    label: "Content",
    collapsible: true,
    storageKey: "sidebar.collapsed.content",
    defaultCollapsed: false,
    items: [
      { path: "/content/erstellen", tKey: "nav.content_create", icon: PenTool },
      { path: "/freigabe", tKey: "nav.approval2", icon: CheckCircle, fallback: "Content-Freigabe" },
      { path: "/wochenplaner", tKey: "nav.weekly_planner", icon: CalendarRange },
      { path: "/kampagnen", tKey: "nav.campaigns", icon: Megaphone },
      { path: "/templates", tKey: "nav.ki_templates", icon: FileText, fallback: "KI-Vorlagen" },
      { path: "/marketplace", tKey: "nav.marketplace2", icon: Store, fallback: "Vorlagen-Marktplatz" },
      { path: "/inbox", tKey: "nav.inbox", icon: Inbox },
      { path: "/performance", tKey: "nav.performance", icon: BarChart3 },
      { path: "/leaderboard", tKey: "nav.lead_performance", icon: Trophy, fallback: "Lead-Performance" },
      { path: "/ab-tests", tKey: "nav.ab_tests", icon: Users },
      { path: "/wettbewerber", tKey: "nav.competition", icon: Crosshair, fallback: "Wettbewerb" },
      { path: "/kalender", tKey: "nav.calendar", icon: Calendar },
    ]
  },
  {
    label: "Anzeigen",
    collapsible: true,
    storageKey: "sidebar.collapsed.google",
    defaultCollapsed: true,
    items: [
      { path: "/google-ads", tKey: "nav.ads_hub", icon: Megaphone, fallback: "Anzeigen (Google & Meta)" },
    ]
  },
  {
    label: "Website & SEO",
    collapsible: true,
    storageKey: "sidebar.collapsed.webseo",
    defaultCollapsed: true,
    items: [
      { path: "/cockpit", tKey: "nav.cockpit", icon: Compass, adminOnly: true, fallback: "Marketing-Cockpit" },
      { path: "/web-analytics", tKey: "nav.website_analytics", icon: Globe, fallback: "Website Analytics" },
      { path: "/seo", tKey: "nav.seo_keywords", icon: LineChart, fallback: "SEO & Keywords" },
    ]
  },
  {
    label: "Leads",
    collapsible: true,
    storageKey: "sidebar.collapsed.leads",
    defaultCollapsed: true,
    items: [
      { path: "/leads", tKey: "nav.all_leads", icon: UserPlus, fallback: "Alle Leads" },
      { path: "/firmen-radar", tKey: "nav.firmenradar", icon: Radar, fallback: "Firmenradar" },
    ]
  },
  {
    label: "Einstellungen",
    collapsible: true,
    storageKey: "sidebar.collapsed.settings2",
    defaultCollapsed: true,
    items: [
      { path: "/admin/users", tKey: "nav.team_users", icon: UserCog, adminOnly: true, fallback: "Team & Benutzer" },
      { path: "/team", tKey: "nav.team", icon: Users },
      { path: "/team/analytics", tKey: "nav.team_analytik", icon: LineChart, fallback: "Team-Analytik" },
      { path: "/social-media", tKey: "nav.social_connections", icon: Share2, fallback: "Social-Media-Verbindungen" },
      { path: "/admin/verbindungen", tKey: "nav.admin_connections", icon: Share2, adminOnly: true, fallback: "Alle Verbindungen (Admin)" },
      { path: "/webhooks", tKey: "nav.webhooks", icon: Webhook },
      { path: "/admin/llm-keys", tKey: "nav.api_keys", icon: KeyRound, adminOnly: true, fallback: "API-Schluessel" },
      { path: "/admin/oauth-credentials", tKey: "nav.apps_integrations", icon: ShieldCheck, adminOnly: true, fallback: "Apps / Integrationen" },
      { path: "/provider", tKey: "nav.model_routing", icon: Cpu, fallback: "Model Routing" },
      { path: "/admin/brand-kit", tKey: "nav.admin_brand_kit", icon: Palette, adminOnly: true, fallback: "Brand Kit" },
      { path: "/admin/activity-log", tKey: "nav.audit_log", icon: Activity, adminOnly: true, fallback: "Audit Log" },
      { path: "/admin/email", tKey: "nav.admin_email", icon: Mail, adminOnly: true },
      { path: "/admin/feedback", tKey: "nav.feedback_inbox", icon: MessageSquareHeart, adminOnly: true, fallback: "Feedback-Postfach" },
    ]
  },
];

export default function Sidebar({ mobileOpen = false, onMobileClose = () => {} }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, lang, setLang, languages } = useLanguage();
  const { dark, toggle: toggleTheme } = useTheme();

  // Close mobile sidebar on route change
  useEffect(() => {
    onMobileClose();
  }, [location.pathname]); // eslint-disable-line

  const handleNavigate = (path) => {
    navigate(path);
    onMobileClose();
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Persisted collapse state per section
  const [collapsed, setCollapsed] = useState(() => {
    const init = {};
    for (const s of navSections) {
      if (s.storageKey) {
        try {
          const stored = localStorage.getItem(s.storageKey);
          init[s.storageKey] = stored === null ? (s.defaultCollapsed ?? true) : stored === "1";
        } catch { init[s.storageKey] = s.defaultCollapsed ?? true; }
      }
    }
    return init;
  });
  const toggleSection = (key) => {
    setCollapsed((c) => {
      const next = { ...c, [key]: !c[key] };
      try { localStorage.setItem(key, next[key] ? "1" : "0"); } catch {}
      return next;
    });
  };

  const cycleLang = () => {
    const idx = languages.findIndex((l) => l.code === lang);
    const next = languages[(idx + 1) % languages.length];
    setLang(next.code);
  };

  const currentFlag = languages.find((l) => l.code === lang)?.flag || "CH";

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={onMobileClose}
          data-testid="sidebar-overlay"
        />
      )}
      <aside
        className={`fixed left-0 top-0 h-screen w-64 bg-white text-slate-900 border-r border-slate-200 flex flex-col z-50 transition-transform duration-300 ease-in-out ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-6 flex items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <img src="/invictaflow-logo.png" alt="InvictaFlow" className="w-9 h-9 rounded-lg" />
            <div className="leading-tight">
              <span className="font-heading text-lg font-bold tracking-tight block">InvictaFlow</span>
              <span className="text-[9px] text-slate-400 tracking-[0.25em] uppercase">InvictaX Group</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button
              onClick={onMobileClose}
              data-testid="sidebar-close-btn"
              className="md:hidden p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
              aria-label="Menü schließen"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

      <Separator className="bg-slate-200 mx-3" />

      {/* Navigation */}
      <nav className="flex-1 py-3 px-3 space-y-1 overflow-y-auto">
        {navSections.map((section, si) => {
          const isCollapsible = !!section.collapsible;
          const isCollapsed = isCollapsible && !!collapsed[section.storageKey];
          // Hide collapsible header if all items are admin-only filtered out (unlikely)
          const visibleItems = section.items.filter((it) => !it.adminOnly || user?.role === "admin");
          if (visibleItems.length === 0) return null;
          // Auto-expand if user navigates to a child path
          const sectionActive = visibleItems.some((it) =>
            location.pathname === it.path || (it.path !== "/" && location.pathname.startsWith(it.path)),
          );
          const showItems = !isCollapsed || sectionActive;
          return (
            <div key={si}>
              {(section.label || section.tKey) && (
                isCollapsible ? (
                  <button
                    type="button"
                    onClick={() => toggleSection(section.storageKey)}
                    data-testid={`section-toggle-${section.storageKey}`}
                    className="w-full flex items-center justify-between px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider text-slate-400 font-semibold hover:text-slate-600 transition-colors"
                  >
                    <span>{section.label || t(section.tKey)}</span>
                    {isCollapsed && !sectionActive ? (
                      <ChevronRight className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )}
                  </button>
                ) : (
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-3 pt-3 pb-1">
                    {section.label || t(section.tKey)}
                  </p>
                )
              )}
              {showItems && visibleItems.map(({ path, tKey, icon: Icon, fallback }) => {
                const tLabel = t(tKey);
                const label = (tLabel === tKey && fallback) ? fallback : tLabel;
                const active = location.pathname === path ||
                  (path !== "/" && location.pathname.startsWith(path));
                return (
                  <button
                    key={path}
                    onClick={() => handleNavigate(path)}
                    data-testid={`nav-${tKey.replace("nav.", "")}`}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] transition-all duration-150 ${
                      active
                        ? "bg-[#2563EB] text-white font-medium shadow-sm"
                        : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {label}
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>

      <Separator className="bg-slate-200 mx-3" />

      {/* Language + Theme */}
      <div className="px-4 py-2 space-y-1">
        <button
          onClick={cycleLang}
          data-testid="language-switcher"
          className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <Globe className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1 text-left">{languages.find((l) => l.code === lang)?.label}</span>
          <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono">{currentFlag}</span>
        </button>
        <button
          onClick={toggleTheme}
          data-testid="theme-toggle"
          className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        >
          {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          <span>{dark ? "Light Mode" : "Dark Mode"}</span>
        </button>
      </div>

      {/* User */}
      <div className="px-4 pb-4">
        <div className="flex items-center gap-3 mb-3">
          <Avatar className="w-8 h-8">
            <AvatarImage src={user?.picture} />
            <AvatarFallback className="bg-blue-100 text-blue-700 text-xs">
              {user?.name?.[0] || "A"}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name || "Admin"}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4" />
          {t("nav.logout")}
        </button>
      </div>
    </aside>
    </>
  );
}
